import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Monitor,
  Plus,
  Trash2,
  Play,
  ExternalLink,
  Copy,
  Check,
} from 'lucide-react';
import { api } from '../api/client';
import {
  Button,
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Skeleton,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Checkbox,
  Label,
} from '@databro/ui';

export default function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedActionIds, setSelectedActionIds] = useState<string[]>([]);
  const [copiedId, setCopiedId] = useState(false);

  // Fetch client details
  const { data: client, isLoading } = useQuery({
    queryKey: ['client', id],
    queryFn: () => api.getClient(id!),
    enabled: !!id,
  });

  // Fetch all actions for assignment modal
  const { data: allActions } = useQuery({
    queryKey: ['actions'],
    queryFn: () => api.getActions(),
  });

  // Assign actions mutation
  const assignMutation = useMutation({
    mutationFn: (actionIds: string[]) => api.assignActionsToClient(id!, actionIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client', id] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['actions'] });
      setShowAssignModal(false);
      setSelectedActionIds([]);
    },
  });

  // Remove action mutation
  const removeMutation = useMutation({
    mutationFn: (actionId: string) => api.removeActionFromClient(id!, actionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client', id] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['actions'] });
    },
  });

  const handleCopyId = () => {
    if (client) {
      navigator.clipboard.writeText(client.id);
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000);
    }
  };

  const handleOpenAssignModal = () => {
    // Pre-select already assigned actions
    if (client) {
      setSelectedActionIds(client.actions.map((a) => a.id));
    }
    setShowAssignModal(true);
  };

  const handleToggleAction = (actionId: string) => {
    setSelectedActionIds((prev) =>
      prev.includes(actionId)
        ? prev.filter((id) => id !== actionId)
        : [...prev, actionId]
    );
  };

  const handleAssign = () => {
    // Find actions to add (selected but not currently assigned)
    const currentIds = client?.actions.map((a) => a.id) || [];
    const toAdd = selectedActionIds.filter((id) => !currentIds.includes(id));

    if (toAdd.length > 0) {
      assignMutation.mutate(toAdd);
    } else {
      setShowAssignModal(false);
    }
  };

  // Get actions not yet assigned to this client
  const assignedActionIds = client?.actions.map((a) => a.id) || [];
  const availableActions = allActions?.filter((a) => !assignedActionIds.includes(a.id)) || [];

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="space-y-4">
        <Link to="/clients" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" />
          Back to Clients
        </Link>
        <div className="border rounded-lg py-8 text-center">
          <p className="text-muted-foreground">Client not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Link to="/clients" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Clients
          </Link>
          <div className="flex items-center gap-3">
            <Monitor className="h-6 w-6 text-muted-foreground" />
            <h1 className="text-2xl font-bold">{client.name || 'Unnamed Client'}</h1>
            <div className="flex items-center gap-2">
              <span
                className={`inline-block w-2 h-2 rounded-full ${
                  client.isOnline ? 'bg-green-500' : 'bg-muted-foreground/30'
                }`}
              />
              <Badge variant={client.isOnline ? 'success' : 'secondary'}>
                {client.isOnline ? 'Online' : 'Offline'}
              </Badge>
            </div>
          </div>
        </div>
        <Button onClick={handleOpenAssignModal}>
          <Plus className="h-4 w-4 mr-2" />
          Assign Actions
        </Button>
      </div>

      {/* Client Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Client Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-muted-foreground mb-1">Client ID</div>
              <div className="flex items-center gap-2">
                <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
                  {client.id.slice(0, 8)}...
                </code>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={handleCopyId}>
                  {copiedId ? (
                    <Check className="h-3 w-3 text-green-500" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </Button>
              </div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">Created</div>
              <div>{new Date(client.createdAt).toLocaleDateString()}</div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">Last Seen</div>
              <div>
                {client.lastSeenAt
                  ? new Date(client.lastSeenAt).toLocaleString()
                  : 'Never'}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">Actions Assigned</div>
              <div>{client.actions.length}</div>
            </div>
          </div>
          {client.userAgent && (
            <div className="mt-4 pt-4 border-t">
              <div className="text-muted-foreground text-sm mb-1">User Agent</div>
              <div className="text-xs text-muted-foreground font-mono bg-muted p-2 rounded">
                {client.userAgent}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assigned Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Assigned Actions</CardTitle>
          <CardDescription>
            Actions that this client can execute
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Platform</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {client.actions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                    No actions assigned yet. Click "Assign Actions" to add some.
                  </TableCell>
                </TableRow>
              )}
              {client.actions.map((action) => (
                <TableRow key={action.id}>
                  <TableCell>
                    <Link
                      to={`/actions/${action.slug}`}
                      className="font-medium hover:underline flex items-center gap-2"
                    >
                      {action.name}
                      <ExternalLink className="h-3 w-3 text-muted-foreground" />
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{action.platform}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={action.enabled ? 'success' : 'secondary'}>
                      {action.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        asChild
                      >
                        <Link to={`/actions/${action.slug}`}>
                          <Play className="h-4 w-4 mr-1" />
                          View
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => {
                          if (confirm('Remove this action from the client?')) {
                            removeMutation.mutate(action.id);
                          }
                        }}
                        disabled={removeMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Assign Actions Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Assign Actions</DialogTitle>
            <DialogDescription>
              Select actions to assign to this client for execution.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 max-h-[400px] overflow-y-auto">
            {availableActions.length === 0 && client.actions.length === 0 && (
              <div className="text-center text-muted-foreground py-4">
                No actions available. Create some actions first.
              </div>
            )}
            {availableActions.length === 0 && client.actions.length > 0 && (
              <div className="text-center text-muted-foreground py-4">
                All actions are already assigned to this client.
              </div>
            )}
            {availableActions.map((action) => (
              <div
                key={action.id}
                className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer"
                onClick={() => handleToggleAction(action.id)}
              >
                <Checkbox
                  id={`action-${action.id}`}
                  checked={selectedActionIds.includes(action.id)}
                  onCheckedChange={() => handleToggleAction(action.id)}
                />
                <div className="flex-1">
                  <Label
                    htmlFor={`action-${action.id}`}
                    className="font-medium cursor-pointer"
                  >
                    {action.name}
                  </Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-xs">
                      {action.platform}
                    </Badge>
                    {!action.enabled && (
                      <Badge variant="secondary" className="text-xs">
                        Disabled
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAssignModal(false);
                setSelectedActionIds([]);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAssign}
              disabled={
                assignMutation.isPending ||
                selectedActionIds.filter((id) => !assignedActionIds.includes(id)).length === 0
              }
            >
              {assignMutation.isPending ? 'Assigning...' : 'Assign Selected'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
